import { useEffect, useState } from "react";
import api from "@/lib/api";
import { formatApiError } from "@/lib/format";
import { DropZone } from "@/components/undertake/DropZone";
import { UploadProgress, useProofUploads } from "@/components/undertake/UploadProgress";
import { ProofButton } from "@/components/undertake/ProofButton";
import { FileList } from "@/components/undertake/UndertakeModal";
import { openFile } from "@/components/undertake/ReviewTab";
import { toast } from "sonner";

export function XpProofModal({ index, entry, request, onClose, onChanged }) {
  const [r, setR] = useState(request && ["draft", "pending"].includes(request.status) ? request : null);
  const [busy, setBusy] = useState(false);
  const declined = request?.status === "declined" ? request : null;

  useEffect(() => {
    if (entry.verified || r) return;
    api.post("/xp-requests", { index }).then(({ data }) => setR(data)).catch((err) => { toast.error(formatApiError(err.response?.data?.detail)); onClose(); });
  }, [entry.verified, index, r, onClose]);

  const { items: progress, upload, busy: uploading } = useProofUploads("xp", r?.id, (data) => setR((p) => ({ ...p, files: [...p.files, data] })));

  const remove = async (f) => {
    try { await api.delete(`/xp-requests/${r.id}/files/${f.id}`); setR((p) => ({ ...p, files: p.files.filter((x) => x.id !== f.id) })); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const send = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/xp-requests/${r.id}/send`); setR(data); onChanged(); toast.success("sent to the members for verification"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const pending = r?.status === "pending";
  const close = () => { if (uploading > 0 && !window.confirm("an upload is still running. leave and cancel it?")) return; onClose(); };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6 fade-in" style={{ background: "rgba(0,0,0,0.75)" }} onClick={close} data-testid="xp-proof-overlay">
      <div className="w-full max-w-lg p-8 max-h-[90vh] overflow-y-auto" style={{ background: "var(--bg-modal)", border: "1px solid var(--border)" }} onClick={(e) => e.stopPropagation()} data-testid="xp-proof-modal">
        <div className="flex justify-between items-start mb-6">
          <span className="text-xs uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>verify natural xp</span>
          <button onClick={close} data-testid="xp-proof-close" className="text-sm opacity-60 hover:opacity-100" style={{ color: "var(--text-primary)" }}>[ close ]</button>
        </div>
        <p className="font-head text-xl mb-2" style={{ color: "var(--text-primary)" }} data-testid="xp-proof-title">{entry.title}</p>
        {entry.description && <p className="long-text-sm text-sm mb-6" style={{ color: "var(--text-secondary)" }}>{entry.description}</p>}

        {entry.verified ? (
          <p className="text-sm tracking-widest" style={{ color: "var(--accent)" }} data-testid="xp-proof-verified">verified</p>
        ) : !r ? (
          <p className="text-xs" style={{ color: "var(--text-muted)" }}>…</p>
        ) : pending ? (
          <>
            <p className="text-sm tracking-widest mb-4" style={{ color: "var(--accent)" }} data-testid="xp-proof-pending">awaiting member review</p>
            <ul className="flex flex-col gap-2">
              {r.files.map((f) => (
                <li key={f.id}><button onClick={() => openFile(r.id, f, "xp-requests")} className="btn w-full text-left px-3 py-2 text-xs" data-testid={`xp-proof-sent-file-${f.id}`}>↓ {f.name}</button></li>
              ))}
            </ul>
          </>
        ) : (
          <>
            {declined && <p className="text-xs mb-4 p-3" style={{ border: "1px solid var(--accent)", color: "var(--accent)" }} data-testid="xp-proof-declined">declined: {declined.reason}</p>}
            <p className="text-xs mb-2 flex items-center gap-2" style={{ color: "var(--text-muted)" }}>attach <ProofButton testId="xp-proof-btn" /></p>
            <p className="text-[10px] tracking-widest mb-4" style={{ color: "var(--text-muted)" }}>up to 10 files · 500 mb each · keep this window open while uploading</p>
            <DropZone onFiles={upload} disabled={r.files.length >= 10} />
            {uploading > 0 && <p className="text-xs mt-3" style={{ color: "var(--accent)" }} data-testid="xp-proof-uploading">uploading {uploading}…</p>}
            <UploadProgress items={progress} />
            <FileList files={r.files} onRemove={remove} />
            <button onClick={send} disabled={busy || uploading > 0 || !r.files.length} data-testid="xp-proof-send" className="btn w-full py-3 mt-6 text-sm tracking-widest">
              {busy ? "…" : "SEND FOR VERIFICATION"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
